import Link from 'next/link'
import React, { ComponentPropsWithoutRef } from 'react'
import { getCategoriesData } from '~shared/api/categories/getCategories'
import { Program } from '~shared/api/categories/types'
import { SearchInput } from '~shared/components/search-input'
import { Select } from '~shared/components/select'
import { cn } from '~shared/utils/cn'
import { ProgramCard } from '~widgets/program-card/components/program-card'
import { filteredPrograms, programsFilter } from '../const/programs-filter'
import { Button } from '~shared/components/button-v2'
import { MobileFilter } from '~widgets/mobile-filter/components/mobile-filter'
import { Close } from '~shared/icons/close'

interface ProgramsSectionProps extends ComponentPropsWithoutRef<'section'> {
  categories: Awaited<ReturnType<typeof getCategoriesData>>
}

interface ProgramWithColor extends Program {
  duration: number
  format: string
  faculty: Program['faculty'] & {
    color: string | null | undefined
  }
}
type TSearchParams = {
  duration: { slug: string; name: string }
  faculty: { slug: string; name: string }
  category: { slug: string; name: string }
  format: { slug: string; name: string }
}

export const ProgramsSection: React.FC<ProgramsSectionProps> = props => {
  const { className = '', categories } = props
  const [searchParams, setSearchParams] = React.useState<TSearchParams>({
    duration: { slug: 'all', name: '' },
    faculty: { slug: 'all', name: '' },
    category: { slug: 'all', name: '' },
    format: { slug: 'all', name: '' }
  })

  const [showedPrograms, setShowedPrograms] = React.useState(6)

  const allPrograms = categories?.categories?.reduce(
    (acc: ProgramWithColor[], category) => {
      const programs =
        category.programs?.map(item => {
          return {
            ...item,
            faculty: {
              ...item.faculty,
              color: categories?.faculties?.filter(
                fac => fac.slug === item.faculty.slug
              )[0]?.color
            },
            duration:
              Number(item.cost?.studyDurationYears) * 12 +
              Number(item?.cost?.studyDurationMonths),
            category: {
              name: category.name,
              slug: category.slug,
              slugSingular: category.slugSingular
            },
            format:
              category.slug === 'gruppa-vyhodnogo-dnya' ? 'hybrid' : 'dist'
          }
        }) || []
      return [...acc, ...programs]
    },
    []
  )
  const activeFilters = Object.keys(searchParams).filter(
    key => searchParams[key as keyof TSearchParams].slug !== 'all'
  )
  const filtered = filteredPrograms(allPrograms ?? [], searchParams)

  return (
    <section className={cn(className, '')}>
      <div className={cn('container container-slim')}>
        <h2 className='typo-section-heading typo-section-heading-mb'>
          Программы обучения
        </h2>
        {activeFilters.length > 0 && (
          <div className='mb-[24px] flex gap-[8px] justify-center flex-wrap'>
            {activeFilters.map(item => (
              <Button
                key={item}
                onClick={() =>
                  setSearchParams(prev => {
                    return {
                      ...prev,
                      [item]: {
                        name: '',
                        slug: 'all'
                      }
                    }
                  })
                }
                className='h-[40px] bg-[#3A52EE1A] duration-300 ease-in-out text-accent px-[12px] hover:bg-accent hover:text-white text-[14px] gap-[8px]'>
                {item === 'duration' ? 'Длительность ' : ''}
                {searchParams[item as keyof TSearchParams].name}
                <Close />
              </Button>
            ))}
            <Button
              className='h-[40px] bg-[#3A52EE1A] duration-300 ease-in-out text-accent px-[12px] hover:bg-accent hover:text-white text-[14px] gap-[8px]'
              onClick={() => {
                setSearchParams({
                  duration: { slug: 'all', name: '' },
                  faculty: { slug: 'all', name: '' },
                  category: { slug: 'all', name: '' },
                  format: { slug: 'all', name: '' }
                })
                setShowedPrograms(6)
              }}>
              Сбросить все
              <Close />
            </Button>
          </div>
        )}
        <div className='hidden md:block xl:hidden mb-[8px]'>
          <SearchInput categories={categories} className='h-[67px]' />
        </div>
        <div className='hidden md:block w-full mb-[20px]'>
          <div className='grid md:grid-cols-[1fr_1fr] lg:grid-cols-[1fr_1fr_1fr_1fr] xl:grid-cols-[1fr_1fr_1fr_1fr_1fr]  gap-[8px] md:gap-[16px] pb-[1px]'>
            <div className='hidden xl:block'>
              <SearchInput categories={categories} className='h-[67px]' />
            </div>
            {programsFilter(categories).map((item, index) => (
              <Select
                searchParams={searchParams}
                selectData={item}
                key={item.filterName.slug}
                setSearchParams={setSearchParams}
                setShowedPrograms={setShowedPrograms}
              />
            ))}
          </div>
        </div>
        <MobileFilter
          searchParams={searchParams}
          categories={categories}
          setSearchParams={setSearchParams}
          setShowedPrograms={setShowedPrograms}
          programsFilter={programsFilter}
        />
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[16px]'>
          {filtered.slice(0, showedPrograms).map(el => (
            <ProgramCard program={el} key={el.slug} />
          ))}
        </div>

        <div className='flex justify-center mt-[40px]'>
          {filtered.length === 0 &&
            Object.values(searchParams).some(el => el.slug !== 'all') && (
              <p className='text-center'>
                Такой программы пока нет. Попробуете изменить запрос?
              </p>
            )}

          {filtered.length > 11 && showedPrograms < 12 ? (
            <Button
              size='slg'
              onClick={() => setShowedPrograms(12)}
              variant='primary'
              className='w-[320px]'>
              Ещё 6 программ
            </Button>
          ) : (
            filtered.length !== 12 &&
            filtered.length > 6 && (
              <Link
                href='/programmy'
                // href={`/programmy?query=${filtered
                //   .map(item => item.slug)
                //   .join('--')}`}
                className='rounded-[24px] bg-black text-white h-[67px] w-[320px] mx-auto flex items-center justify-center'>
                Смотреть все
              </Link>
            )
          )}
        </div>
      </div>
    </section>
  )
}
